import React, { useEffect, useState } from "react";
import Button from "../../Common/FormElements/Button/Button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ValidatePacking from "../ValidatePacking";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import { getDelivery, updateDelivery } from "../../../REDUX/actions/delivery";
import Select from "../../Common/FormElements/Select/Select";
import { statusOptions, useDeparture } from "../useDeparture";
import { documentExtensions } from "../../Produits/NewProductTabContents/data";
import API from "../../../Api/Api";
import moment from "moment";
import { uploadAttachmentToStrapi } from "../../../Utils/uploadAttachmentToStrapi";
import {
  DownloadAddress,
  DownloadPackingList,
  handleDownload,
} from "../../../Utils/deliveryUtils";
import { toast } from "react-toastify";

const headers = (t) => [
  t("Product Name"),
  t("Ref"),
  t("Tank/Panel"),
  t("Serial Number"),
  t("Qty"),
  t("Validation"),
];

const DeparturePreparation = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const { t } = useTranslation("common");
  const { delivery } = useSelector((state) => state.deliveryReducer);
  const { getOptionsData, optionsData } = useDeparture();
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const rawPackings =
    delivery?.attributes?.commande?.data?.attributes?.packings?.data;

  const packings = React.useMemo(() => {
    if (!rawPackings || rawPackings.length === 0) return [];

    const grouped = rawPackings.reduce((acc, packing) => {
      const modelId = packing?.attributes?.model?.data?.id;

      if (!modelId) return acc;

      if (!acc[modelId]) {
        acc[modelId] = {
          ...packing,
          attributes: {
            ...packing.attributes,
            quantity: 1,
            ids: [],
          },
        };
      }

      acc[modelId].attributes.quantity += packing?.attributes?.quantity || 0;

      acc[modelId].attributes.ids.push(packing.id);

      return acc;
    }, {});

    return Object.values(grouped);
  }, [rawPackings]);

  const columns = [
    {
      name: "productName",
      render: (row) => {
        const model = row?.attributes?.model?.data;
        const { productDimensions, weight } =
          model?.attributes?.caracteristique?.data?.attributes || {};

        return (
          <div>
            <p className="mb-3">{model?.attributes?.name ?? ""}</p>
            {productDimensions && (
              <p className="mb-0">
                {t("Pallet dimensions")}
                <br />
                {productDimensions}
              </p>
            )}
            {weight && (
              <p className="mb-0">
                {t("Weight")} {weight} kg
              </p>
            )}
          </div>
        );
      },
    },
    {
      name: "ref",
      render: (row) => row?.attributes?.reference ?? "-",
    },
    {
      name: "Tank/Panel",
      render: (row) =>
        row?.attributes?.cuve && row?.attributes?.panel
          ? [row?.attributes?.cuve, row?.attributes?.panel].join("/")
          : "-",
    },
    {
      name: "serialNumber",
      render: (row) => row?.attributes?.serialNumber ?? "-",
    },
    {
      name: "qte",
      render: (row) => row?.attributes?.quantity ?? 0,
    },
    {
      name: "validation",
      render: (row) => (
        <ValidatePacking
          canUpdate={[1, 3]?.includes(user?.attributes?.userRole)}
          deliveryId={delivery?.id}
          isValidated={row?.attributes?.isPrepared}
          field={"isPrepared"}
          ids={row?.attributes?.ids}
          disabled={delivery?.attributes?.status === "READY"}
        />
      ),
    },
  ];

  const downloadMedia = async (e) => {
    setLoading("MEDIA");
    const attachments = delivery?.attributes?.preparationAttachments?.data;
    if (attachments?.length > 0) {
      await handleDownload(
        attachments,
        `Préparation${
          delivery?.attributes?.number ? `-${delivery?.attributes?.number}` : ""
        }`,
      );
    } else {
      toast.error(t("No files have been added yet."));
    }
    setLoading(false);
  };

  const importMedia = async (e) => {
    setLoading("MEDIA");
    if (e.target.files.length) {
      const file = e.target.files[0];
      if (file instanceof File) {
        const { data: createdAttachment } = await API.post("api/attachments", {
          data: {
            title: file.name,
            date: moment().utc(true).toDate(),
            deliveryDepot: delivery?.id,
          },
        });
        await uploadAttachmentToStrapi(
          file,
          "depot/magasin fichier",
          createdAttachment.data.id,
          "api::attachment.attachment",
          "file",
        );
      }
      await dispatch(getDelivery(delivery?.id, t("File has been added")));
      e.target.value = "";
    }
    setLoading(false);
  };

  useEffect(() => {
    setComment(delivery?.attributes?.preparationComment ?? "");
  }, [delivery?.attributes?.preparationComment]);

  useEffect(() => {
    getOptionsData(["storekeeper"]);
  }, [getOptionsData]);

  return (
    <div className="d-flex flex-column text-dark position-relative text-break">
      {[3, 1].includes(user?.attributes?.userRole) ? (
        <div className="d-flex gap-4 align-items-center mb-3">
          <div>
            <Select
              name="storekeeper"
              showClear
              value={
                delivery?.attributes?.storekeeper?.data
                  ? {
                      name: delivery?.attributes?.storekeeper?.data?.attributes
                        ?.name,
                      code: delivery?.attributes?.storekeeper?.data?.id,
                    }
                  : null
              }
              placeholder={t("Preparator")}
              options={optionsData.storekeeper || []}
              callback={(e) =>
                dispatch(
                  updateDelivery(
                    delivery?.id,
                    {
                      storekeeper: e.value?.code || null,
                    },
                    () =>
                      dispatch(
                        getDelivery(
                          delivery?.id,
                          t("Preparator has been updated"),
                        ),
                      ),
                  ),
                )
              }
            />
          </div>
          <Button
            loading={loading === "LISTING"}
            onClick={async () => {
              setLoading("LISTING");
              await DownloadPackingList(
                delivery?.id,
                delivery?.attributes?.number,
                t,
              );
              setLoading(false);
            }}
          >
            <i className="bi bi-download me-2" />
            {t("Listing")}
          </Button>
          <Button
            loading={loading === "ADDRESS"}
            onClick={async () => {
              setLoading("ADDRESS");
              await DownloadAddress(
                delivery?.id,
                delivery?.attributes?.number,
                t,
              );
              setLoading(false);
            }}
          >
            <i className="bi bi-download me-2" />
            {t("Delivery Address")}
          </Button>
        </div>
      ) : (
        <div className="d-flex gap-4 align-items-end mb-3">
          <div>
            <p className="f15 bold">{t("Product Summary List")}</p>
            <p className="f15 mb-0">
              {t(
                "Your order will be prepared by our storekeeper. Here is the summary of the items scheduled for delivery.",
              )}
            </p>
          </div>
          <Button
            loading={loading === "LISTING"}
            className="ms-auto"
            onClick={async () => {
              setLoading("LISTING");
              await DownloadPackingList(
                delivery?.id,
                delivery?.attributes?.number,
                t,
              );
              setLoading(false);
            }}
          >
            <i className="bi bi-download me-2" />
            {t("Listing")}
          </Button>
        </div>
      )}

      <div className="overflow-x-auto mt-2 flex-grow-1 mb-3">
        <table className="table table-transparent">
          <thead className="table-dimGrey text-nowrap">
            <tr className="border-none">
              {headers(t).map((header, index) => (
                <th
                  key={header}
                  scope="col"
                  className={`py-3 px-4 text-lightBlack sBold f14 ${
                    index > 0 ? "text-center" : ""
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {packings?.map((packing) => (
              <tr key={packing.id}>
                {columns.map(
                  (column, index) =>
                    !column.hidden && (
                      <td
                        key={column.name}
                        className={`py-3 text-nowrap f14 text-lightBlack d-table-cell ${
                          index >= columns.length - 1 ? "ms-auto px-1" : "px-4"
                        } ${index > 0 ? "text-center" : ""}`}
                      >
                        {column.render(packing)}
                      </td>
                    ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {[0, 2, 4]?.includes(user?.attributes?.userRole) ? (
        comment?.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-4">
            <p className="f16 mb-0 sBold">{t("Storekeeper comment")}</p>
            <p className="mb-0 p-2 br5 bg-white border-1 border-lightGrey">
              {comment}
            </p>
          </div>
        )
      ) : (
        <div className="d-flex flex-column gap-3">
          <TextArea
            placeholder={t("Comment")}
            value={comment}
            callback={(e) => setComment(e.target.value)}
          />
          <div className="ms-auto">
            <Button
              onClick={() => {
                dispatch(
                  updateDelivery(
                    delivery?.id,
                    { preparationComment: comment },
                    () => {
                      dispatch(
                        getDelivery(
                          delivery?.id,
                          t("Comment has been updated"),
                        ),
                      );
                    },
                  ),
                );
              }}
            >
              {t("Save")}
            </Button>
          </div>
        </div>
      )}

      <div className="d-flex gap-4 align-items-end">
        <Button
          className="position-relative"
          loading={loading === "MEDIA"}
          onClick={
            [0, 2, 4]?.includes(user?.attributes?.userRole)
              ? downloadMedia
              : null
          }
        >
          <i className="bi bi-download me-2" />
          {[0, 2, 4]?.includes(user?.attributes?.userRole)
            ? t("Download Photo/Video")
            : t("Import Photo/Video")}

          {[1, 3]?.includes(user?.attributes?.userRole) && (
            <label className="position-absolute top-0 start-0 w-100 h-100 pointer">
              <input
                type="file"
                style={{ display: "none" }}
                multiple
                accept={`image/*,video/*, ${documentExtensions.join(",")}`}
                onChange={importMedia}
              />
            </label>
          )}
        </Button>
        <div className="d-flex flex-column w-100 max-w-200px">
          <p className="mb-2">{t("Depot Status")}</p>
          <Select
            name="status"
            disabled={
              [0, 2, 4]?.includes(user?.attributes?.userRole) ||
              !["TO_PREP", "INCIDENT", "READY"]?.includes(
                delivery?.attributes?.status,
              )
            }
            value={statusOptions(t).find(
              (option) => option.code === delivery?.attributes?.status,
            )}
            placeholder={
              statusOptions(t).find(
                (option) => option.code === delivery?.attributes?.status,
              )?.name
            }
            options={statusOptions(t).filter((option) =>
              ["TO_PREP", "INCIDENT", "READY"].includes(option.code),
            )}
            callback={(e) =>
              dispatch(
                updateDelivery(delivery?.id, { status: e.value.code }, () =>
                  dispatch(
                    getDelivery(delivery?.id, t("Status has been changed")),
                  ),
                ),
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DeparturePreparation;
